import { AxiosError } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import type RepositoryResponse from '@/types/repositoryResponse';
import ajax from '@/utils/ajax';
import defaultRepositoryError from '@/utils/defaultRepositoryError';

export interface ChatHistoryResponse {
  query: string;
  response: string;
  reset: boolean;
  stream: boolean;
  session_id: string;
  status: string;
  type: string;
  data: unknown;
  chunked?: boolean;
  chunk_id?: string;
  chunk_index?: number;
  chunk_count?: number;
  chunk_key?: 'data' | 'response';
  chunk_value_json?: boolean;
  original_size_bytes?: number;
}

export const profileChatRepository = {
  async ask({
    host,
    personId,
    query,
    reset,
    stream,
    tools,
    context
  }: {
    host: string;
    personId: string;
    query: string;
    reset: boolean;
    stream: boolean;
    tools?: string[];
    context?: string[];
  }): Promise<RepositoryResponse<ChatHistoryResponse>> {
    try {
      const path = `/api/dugout/patient_profile_chat/query/${personId}/`;
      const response = await ajax.patch({
        url: `${host}${path}`,
        data: {
          query: query,
          reset: reset,
          stream,
          ...(tools && tools.length > 0 && { tools }),
          ...(context && context.length > 0 && { context })
        }
      });

      return {
        data: response.data as ChatHistoryResponse,
        isError: false
      };
    } catch (error) {
      return defaultRepositoryError(error as AxiosError, 'Failed to update person.');
    }
  },
  async abort({ host, sessionId }: { host: string; sessionId: string }) {
    try {
      const path = `/api/dugout/patient_profile_chat/abort/${sessionId}/`;
      await ajax.delete({
        url: `${host}${path}`
      });

      return {
        data: null,
        isError: false
      };
    } catch (error) {
      return defaultRepositoryError(error as AxiosError, 'Failed to abort session.');
    }
  }
};

const ProfileChatRepository = () => {
  const { host } = useContext(AppContext);

  return {
    async ask({
      id,
      query,
      reset,
      stream,
      tools,
      context
    }: {
      id: string;
      query: string;
      reset: boolean;
      stream: boolean;
      tools?: string[];
      context?: string[];
    }): Promise<RepositoryResponse<ChatHistoryResponse>> {
      return profileChatRepository.ask({
        host,
        personId: id,
        query,
        reset,
        stream,
        tools,
        context
      });
    },
    async abort({ sessionId }: { sessionId: string }) {
      return profileChatRepository.abort({ host, sessionId });
    }
  };
};

export default ProfileChatRepository;
